import { useState } from 'react';
import { useGT } from 'gt-react';
import { AS400_IMPORT_MAX_FILES } from '@erp/rpc';
import { useLatestOnly } from './useLatestOnly';
import {
  as400ImportFileToBase64,
  type As400ImportUpload,
} from '@/api/rpc/hooks/inventory-management/useAs400Import';

/**
 * The files a picker chose, read as the import takes them: name and bytes.
 * Clearing the picker reads as no files; more files than an import takes, or
 * a file the browser can't read, sets `readError` and leaves no files rather
 * than some of them. `readFiles` resolves to null when a newer pick superseded
 * it, so a caller can tell "this read found nothing" from "this read lost".
 */
export function useAs400ImportUploads() {
  const gt = useGT();
  const [uploads, setUploads] = useState<As400ImportUpload[]>([]);
  const [readError, setReadError] = useState<string | null>(null);
  // A read that finishes after a newer pick must not put its files back.
  const reads = useLatestOnly();

  async function readFiles(
    fileList: FileList | null,
  ): Promise<As400ImportUpload[] | null> {
    const stamp = reads.start();
    setReadError(null);
    const chosen = [...(fileList ?? [])];
    // Refused whole rather than trimmed: a file quietly left out is a part of
    // the batch that never reaches the preview.
    if (chosen.length > AS400_IMPORT_MAX_FILES) {
      setUploads([]);
      setReadError(
        gt('Choose at most {max} files at once.', {
          context: 'error when too many AS/400 import files are chosen',
          max: String(AS400_IMPORT_MAX_FILES),
        }),
      );
      return [];
    }
    try {
      const read = await Promise.all(
        chosen.map(async (file) => ({
          fileName: file.name,
          contentBase64: await as400ImportFileToBase64(file),
        })),
      );
      if (!reads.isLatest(stamp)) return null;
      setUploads(read);
      return read;
    } catch {
      if (!reads.isLatest(stamp)) return null;
      setUploads([]);
      setReadError(
        gt('Those files could not be read from disk.', {
          context: 'error when the browser cannot read the chosen files',
        }),
      );
      return [];
    }
  }

  return { uploads, readError, readFiles };
}
